import type { QueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import type { Chat, ChatDetailResponse, ChatEvent, ChatStatus } from "@rivets/core";
import { client } from "@/lib/api";
import { qk } from "@/lib/query-keys";
import { sendLocalCancelHint } from "@/lib/dataSource";
import { useChatStore } from "./chatStore";

/**
 * Stop reads as "Stopping…" the moment it is pressed, as on the Mac, and the
 * chat's own status ends it. The status itself is never guessed: the server
 * always resolves a Stop (CONTRACT P8), so the `chat.updated` that follows is
 * the answer. An optimistic "idle" used to hide a run that was in fact still
 * going; gating the press on the round trip left Stop live for as long as the
 * worker took to answer, which read as a press that did nothing.
 */

/** How long "Stopping…" may wait for the status before the chat is read back
 * (C5: no state lasts forever). Mutable for tests. */
export const STOP_CONFIRMATION_MS = { deadline: 30_000 };

interface StoppingState {
  /** Chats whose Stop was pressed and whose run has not been reported ended. */
  ids: Readonly<Record<string, true>>;
}

export const useStoppingChats = create<StoppingState>(() => ({ ids: {} }));

const deadlines = new Map<string, ReturnType<typeof setTimeout>>();

function isActive(status: ChatStatus | undefined): boolean {
  return status === "running" || status === "queued";
}

/** Whether a chat shows "Stopping…": pressed, and still working by the last
 * status this client holds. */
export function useIsStopping(chat: Pick<Chat, "id" | "status"> | undefined): boolean {
  const pressed = useStoppingChats((state) => chat ? state.ids[chat.id] === true : false);
  return pressed && isActive(chat?.status);
}

function beginStopping(queryClient: QueryClient, chat: Pick<Chat, "id" | "worktreeId">) {
  useStoppingChats.setState((state) => ({ ids: { ...state.ids, [chat.id]: true } }));
  const existing = deadlines.get(chat.id);
  if (existing) clearTimeout(existing);
  deadlines.set(chat.id, setTimeout(() => {
    deadlines.delete(chat.id);
    if (!useStoppingChats.getState().ids[chat.id]) return;
    // The status never came (a frame lost with the socket, say). Read the
    // chat back rather than show "Stopping…" forever; whatever it says now is
    // the truth, a run still going included.
    endStopping(chat.id);
    void queryClient.invalidateQueries({ queryKey: qk.chat(chat.id) });
    void queryClient.invalidateQueries({ queryKey: qk.worktree(chat.worktreeId) });
  }, STOP_CONFIRMATION_MS.deadline));
}

function endStopping(chatId: string) {
  const timer = deadlines.get(chatId);
  if (timer) clearTimeout(timer);
  deadlines.delete(chatId);
  if (!useStoppingChats.getState().ids[chatId]) return;
  useStoppingChats.setState((state) => {
    const { [chatId]: _ended, ...ids } = state.ids;
    return { ids };
  });
}

/** An authoritative status arrived (`chat.updated`). One that is no longer
 * working is the answer to a Stop. */
export function confirmChatStatus(chat: Pick<Chat, "id" | "status">): void {
  if (!isActive(chat.status)) endStopping(chat.id);
}

/** Drops every pending Stop: the organization or account they belong to is gone. */
export function clearStopping(): void {
  for (const timer of deadlines.values()) clearTimeout(timer);
  deadlines.clear();
  useStoppingChats.setState({ ids: {} });
}

const ACTIVE_JOB_STATUSES = new Set(["queued", "assigned", "running"]);

/** The job a stop targets: the chat's newest job that has not reached a
 * terminal status, from what this client already holds. */
export function activeJobId(queryClient: QueryClient, chatId: string): string | undefined {
  const jobs = queryClient.getQueryData<ChatDetailResponse>(qk.chat(chatId))?.jobs;
  const running = jobs
    ?.filter((job) => ACTIVE_JOB_STATUSES.has(job.status))
    .sort((a, b) => b.createdAt - a.createdAt)[0];
  if (running) return running.id;
  return activeJobFromEvents(useChatStore.getState().chats[chatId]?.events ?? []);
}

export function activeJobFromEvents(events: readonly ChatEvent[]): string | undefined {
  for (let index = events.length - 1; index >= 0; index--) {
    const event = events[index]!;
    if (event.payload.kind !== "job.status" || !event.jobId) continue;
    const status = event.payload.status;
    return status === "completed" || status === "failed" || status === "canceled" ? undefined : event.jobId;
  }
  return undefined;
}

/**
 * One Stop press. The job is captured once, at the press, from what this
 * client holds: the same id goes to the LocalApi hint and the relay cancel, so
 * the two can never stop different runs, and a run someone starts after the
 * press is not this press's to cancel. With no job in hand the cancel names
 * none and the server stops whatever is active on the chat — what the user
 * was looking at — rather than this client asking first.
 */
export async function stopChat(
  queryClient: QueryClient,
  chat: Pick<Chat, "id" | "worktreeId">,
  api: Pick<typeof client, "cancelChat"> = client,
): Promise<void> {
  const jobId = activeJobId(queryClient, chat.id);
  beginStopping(queryClient, chat);
  try {
    // Same machine: the daemon can stop the job before the relay round trip
    // lands. A hint only; the relay cancel for the same job always goes too.
    if (jobId) sendLocalCancelHint(chat.worktreeId, chat.id, jobId);
    await api.cancelChat(chat.id, jobId ? { jobId } : {});
  } catch (error) {
    endStopping(chat.id);
    throw error;
  }
}
